/**
 * Canvas operations: turning "what the board is now" into "what the user just did", and back.
 *
 * The canvas used to PUT its whole graph behind a version guard, which makes the unit of
 * concurrency the entire board — two people dragging different cards inside the same second
 * meant the second save was refused and that change was discarded. Sending operations fixes
 * that, and the same message is what gets broadcast to everyone else, so live editing comes out
 * of the same change rather than being layered on top.
 *
 * The ops are DERIVED by diffing rather than emitted by hand at each call site. Hand-emitting
 * reads better in a plan and fails worse in practice: the canvas changes its graph in fourteen
 * places, and the first one someone adds without remembering to emit an op is a change that
 * silently never saves. A diff cannot be forgotten. It also coalesces for free — a drag that
 * fires sixty position updates is one `node.move`, because the diff runs once at flush.
 *
 * Pure and dependency-free on purpose: this is the riskiest code in the collaboration work and
 * it is the part a test can actually pin down.
 */

/** Positions are compared at whole pixels — sub-pixel drift is not a change worth sending. */
const round = (n) => Math.round(Number(n) || 0);

const byId = (rows) => {
    const map = new Map();
    for (const row of rows ?? []) {
        if (row?.id != null) map.set(row.id, row);
    }

    return map;
};

const samePosition = (a, b) =>
    round(a?.position?.x) === round(b?.position?.x) && round(a?.position?.y) === round(b?.position?.y);

/** parentId is normalised: absent, null and '' all mean "not nested". */
const parentOf = (node) => (node?.parentId === undefined || node?.parentId === '' ? null : node.parentId);

const sameData = (a, b) => JSON.stringify(a?.data ?? {}) === JSON.stringify(b?.data ?? {});

/**
 * What changed between two server-shaped graphs, as operations.
 *
 * @param {{nodes: array, edges: array}} before
 * @param {{nodes: array, edges: array}} after
 * @returns {Array<{kind: string, payload: object}>}
 */
export function diffGraph(before, after) {
    const ops = [];

    const oldNodes = byId(before?.nodes);
    const newNodes = byId(after?.nodes);

    // Removals first: a later move for a card that has gone would be applied against a graph
    // that still has it, and the server's skip rule would quietly drop the wrong one.
    for (const id of oldNodes.keys()) {
        if (!newNodes.has(id)) ops.push({ kind: 'node.remove', payload: { id } });
    }

    for (const [id, node] of newNodes) {
        const old = oldNodes.get(id);

        if (!old) {
            ops.push({ kind: 'node.add', payload: { node } });
            continue;
        }

        // Position and nesting travel together: a card dropped into a group changed both, and
        // splitting them puts it briefly at the wrong place inside the wrong parent.
        if (!samePosition(old, node) || parentOf(old) !== parentOf(node)) {
            ops.push({
                kind: 'node.move',
                payload: {
                    id,
                    x: round(node.position?.x),
                    y: round(node.position?.y),
                    parentId: parentOf(node),
                },
            });
        }

        if ((old.width ?? null) !== (node.width ?? null) || (old.height ?? null) !== (node.height ?? null)) {
            ops.push({ kind: 'node.resize', payload: { id, width: node.width ?? 0, height: node.height ?? 0 } });
        }

        if (!sameData(old, node)) {
            ops.push({ kind: 'node.update', payload: { id, data: node.data ?? {} } });
        }
    }

    const oldEdges = byId(before?.edges);
    const newEdges = byId(after?.edges);

    for (const id of oldEdges.keys()) {
        if (!newEdges.has(id)) ops.push({ kind: 'edge.remove', payload: { id } });
    }

    for (const [id, edge] of newEdges) {
        const old = oldEdges.get(id);
        if (!old) {
            ops.push({ kind: 'edge.add', payload: { edge } });
        } else if (old.kind !== edge.kind) {
            ops.push({ kind: 'edge.retype', payload: { id, kind: edge.kind } });
        }
    }

    return ops;
}

/**
 * Apply operations someone else made to this client's graph.
 *
 * Mirrors the server's applier exactly, including the rule that matters most: an op naming a
 * card that isn't here is SKIPPED. Two people on one canvas race constantly, and a remote
 * `node.move` for a card this client already deleted must not resurrect it.
 *
 * @param {array} nodes  server-shaped nodes
 * @param {array} edges
 * @param {Array<{kind: string, payload: object}>} ops
 * @returns {{nodes: array, edges: array, changed: boolean}}
 */
export function applyOps(nodes, edges, ops) {
    let nextNodes = nodes ?? [];
    let nextEdges = edges ?? [];
    let changed = false;

    const findIndex = (rows, id) => rows.findIndex((row) => row?.id === id);

    for (const op of ops ?? []) {
        const kind = op?.kind;
        const payload = op?.payload ?? {};

        switch (kind) {
            case 'node.add': {
                const node = payload.node;
                if (!node?.id || findIndex(nextNodes, node.id) !== -1) break;
                nextNodes = [...nextNodes, node];
                changed = true;
                break;
            }
            case 'node.move': {
                const i = findIndex(nextNodes, payload.id);
                if (i === -1) break;
                const node = { ...nextNodes[i], position: { x: payload.x, y: payload.y } };
                if ('parentId' in payload) {
                    if (payload.parentId) node.parentId = payload.parentId;
                    else delete node.parentId;
                }
                nextNodes = nextNodes.map((n, index) => (index === i ? node : n));
                changed = true;
                break;
            }
            case 'node.resize': {
                const i = findIndex(nextNodes, payload.id);
                if (i === -1) break;
                nextNodes = nextNodes.map((n, index) => (index === i
                    ? { ...n, width: payload.width || null, height: payload.height || null }
                    : n));
                changed = true;
                break;
            }
            case 'node.update': {
                const i = findIndex(nextNodes, payload.id);
                if (i === -1) break;
                // Shallow MERGE: the node's data carries keys this op knows nothing about.
                nextNodes = nextNodes.map((n, index) => (index === i
                    ? { ...n, data: { ...(n.data ?? {}), ...(payload.data ?? {}) } }
                    : n));
                changed = true;
                break;
            }
            case 'node.remove': {
                if (findIndex(nextNodes, payload.id) === -1) break;
                nextNodes = nextNodes
                    .filter((n) => n.id !== payload.id)
                    // A child pointing at a parent that has gone is dropped by the canvas
                    // silently, so removing a group would take its contents with it.
                    .map((n) => {
                        if (n.parentId !== payload.id) return n;
                        const { parentId, ...rest } = n;

                        return rest;
                    });
                nextEdges = nextEdges.filter((e) => e.source !== payload.id && e.target !== payload.id);
                changed = true;
                break;
            }
            case 'edge.add': {
                const edge = payload.edge;
                if (!edge?.id || findIndex(nextEdges, edge.id) !== -1) break;
                nextEdges = [...nextEdges, edge];
                changed = true;
                break;
            }
            case 'edge.retype': {
                const i = findIndex(nextEdges, payload.id);
                if (i === -1) break;
                nextEdges = nextEdges.map((e, index) => (index === i ? { ...e, kind: payload.kind } : e));
                changed = true;
                break;
            }
            case 'edge.remove': {
                if (findIndex(nextEdges, payload.id) === -1) break;
                nextEdges = nextEdges.filter((e) => e.id !== payload.id);
                changed = true;
                break;
            }
            default:
                // An unknown kind is ignored, never guessed at — a newer client's vocabulary
                // reaching an older one is a normal deploy, not an error.
                break;
        }
    }

    return { nodes: nextNodes, edges: nextEdges, changed };
}
